import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Minus,
  Plus,
  X,
  Maximize2,
  Minimize2,
  RotateCcw,
  Move,
  Search,
  Download,
  Grid,
  Sparkles,
  Wine,
  Coffee,
  Utensils,
  Star,
} from "lucide-react";

const MenuGallery = () => {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");

  const menuImages = [
    {
      id: 1,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1766415955/Golf-2_page-0001_m02f6y.jpg",
      category: "main",
      icon: <Utensils className="w-5 h-5" />,
    },
    {
      id: 2,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1766415952/Golf-3_page-0001_cucmmf.jpg",
      category: "beverage",
      icon: <Coffee className="w-5 h-5" />,
    },
    {
      id: 3,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1766415950/Golf-4_page-0001_oef3xa.jpg",
      category: "dessert",
      icon: <Star className="w-5 h-5" />,
    },
    {
      id: 4,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1766415950/Golf-5_page-0001_o2dcnc.jpg",
      category: "breakfast",
      icon: <Coffee className="w-5 h-5" />,
    },
    {
      id: 5,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1766415949/Golf-6_page-0001_zkq1cv.jpg",
      category: "lunch",
      icon: <Utensils className="w-5 h-5" />,
    },
    {
      id: 6,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1766415949/Golf-7_page-0001_dvrfah.jpg",
      category: "dinner",
      icon: <Utensils className="w-5 h-5" />,
    },
    {
      id: 7,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1766415949/Golf-8_page-0001_zlajpi.jpg",
      category: "wine",
      icon: <Wine className="w-5 h-5" />,
    },
    {
      id: 8,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1766415949/Golf-9_page-0001_dxkasc.jpg",
      category: "cocktail",
      icon: <Wine className="w-5 h-5" />,
    },
    {
      id: 9,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1766415950/Golf-10_page-0001_jhmq69.jpg",
      category: "seasonal",
      icon: <Sparkles className="w-5 h-5" />,
    },
  ];

  const categories = [
    { id: "all", label: "All Menus", icon: <Grid className="w-4 h-4" /> },
    { id: "main", label: "Main", icon: <Utensils className="w-4 h-4" /> },
    {
      id: "beverage",
      label: "Beverages",
      icon: <Coffee className="w-4 h-4" />,
    },
    { id: "dessert", label: "Desserts", icon: <Star className="w-4 h-4" /> },
    { id: "wine", label: "Wine List", icon: <Wine className="w-4 h-4" /> },
  ];

  const filteredImages =
    activeFilter === "all"
      ? menuImages
      : menuImages.filter((img) => img.category === activeFilter);

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 5));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setStartPosition({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const newX = e.clientX - startPosition.x;
    const newY = e.clientY - startPosition.y;

    const maxMove = (zoomLevel - 1) * 100;
    setPosition({
      x: Math.max(Math.min(newX, maxMove), -maxMove),
      y: Math.max(Math.min(newY, maxMove), -maxMove),
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleFullscreen = () => {
    if (!isFullscreen) {
      const element = document.querySelector(".modal-container");
      if (element.requestFullscreen) {
        element.requestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const downloadImage = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `rgcc-menu-${selectedImage?.id || "image"}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        if (isFullscreen) {
          handleFullscreen();
        } else {
          setSelectedImage(null);
          resetZoom();
        }
      }
    };

    if (selectedImage) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [selectedImage, isFullscreen]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-lime-50">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-r from-emerald-900 via-emerald-800 to-lime-800 text-white py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-lime-600/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 text-emerald-200 hover:text-white mb-6 transition-all duration-300 group"
              >
                <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                <span>Back to Restaurant</span>
              </button>

              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-300/20 to-lime-300/20 backdrop-blur-sm border border-emerald-300/30 flex items-center justify-center">
                  <Utensils className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold">
                  Menu Gallery
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 text-emerald-200">
                <Search className="w-5 h-5" />
                <span>Click to explore menus</span>
              </div>
              <div className="w-px h-6 bg-emerald-300/50"></div>
              <div className="flex items-center gap-2 text-emerald-200">
                <Sparkles className="w-5 h-5" />
                <span>{menuImages.length} Menus</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Filter Bar */}
        {/* <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <Filter className="w-5 h-5 text-emerald-600" />
            <span className="font-semibold text-gray-700">
              Filter by Category
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveFilter(category.id)}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300 ${
                  activeFilter === category.id
                    ? "bg-gradient-to-r from-emerald-500 to-lime-500 text-white shadow-lg"
                    : "bg-white text-gray-700 hover:bg-gray-50 shadow hover:shadow-md border border-gray-100"
                }`}
              >
                {category.icon}
                {category.label}
              </button>
            ))}
          </div>
        </div> */}

        {/* Menu Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filteredImages.map((menu) => (
            <div
              key={menu.id}
              className="group relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-pointer transform hover:-translate-y-2"
              onClick={() => setSelectedImage(menu)}
            >
              {/* Image Container */}
              <div className="relative h-64 md:h-72 overflow-hidden">
                <img
                  src={menu.url}
                  alt={`Menu ${menu.id}`}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/30 via-transparent to-transparent"></div>

                {/* Category Badge */}
                {/* <div className="absolute top-4 left-4">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm text-emerald-700 text-sm font-semibold shadow-lg">
                    {menu.icon}
                    {categories.find((c) => c.id === menu.category)?.label ||
                      menu.category}
                  </div>
                </div> */}
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-lg">View Full Menu</span>
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-emerald-500 to-lime-500 flex items-center justify-center">
                      <Search className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Results Count */}
        <div className="mt-8 text-center text-gray-600">
          Showing {filteredImages.length} of {menuImages.length} menus
        </div>

        {/* Quick Actions */}
        <div className="mt-12 bg-gradient-to-r from-emerald-50 to-lime-50 rounded-2xl p-8 border border-emerald-100 shadow-lg">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Need Assistance?
            </h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
              <a
                href="tel:+8801769660126"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-emerald-500 to-lime-500 hover:from-emerald-600 hover:to-lime-600 transition-all duration-300 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl"
              >
                <span>Call: +880 1769 660126</span>
              </a>
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-emerald-600 border-2 border-emerald-500 hover:bg-emerald-50 transition-all duration-300"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Restaurant
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-50 p-4 modal-container"
          onClick={() => {
            setSelectedImage(null);
            resetZoom();
            if (isFullscreen) handleFullscreen();
          }}
        >
          <div
            className="relative w-full h-full max-w-7xl max-h-[90vh] bg-white rounded-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-50 to-lime-50 border-b border-emerald-100 shrink-0">
              {/* <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg shadow-sm">
                  {selectedImage.icon}
                  <span className="text-sm font-semibold text-emerald-700">
                    {categories.find((c) => c.id === selectedImage.category)
                      ?.label || "Menu"}
                  </span>
                </div>
              </div> */}

              <div className="flex items-center gap-2">
                {/* Download Button */}
                <button
                  onClick={() => downloadImage(selectedImage.url)}
                  className="p-3 hover:bg-white rounded-xl transition-colors duration-300 group"
                  title="Download Menu"
                >
                  <Download className="w-5 h-5 text-gray-600 group-hover:text-emerald-600" />
                </button>

                {/* Zoom Controls */}
                <div className="flex items-center gap-1 bg-white rounded-xl p-1 shadow-sm">
                  <button
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 0.5}
                    className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Zoom Out"
                  >
                    <Minus className="w-4 h-4" />
                  </button>

                  <button
                    onClick={resetZoom}
                    className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-w-[70px]"
                    title="Reset Zoom"
                  >
                    {Math.round(zoomLevel * 100)}%
                  </button>

                  <button
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 5}
                    className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Zoom In"
                  >
                    <Plus className="w-4 h-4" />
                  </button>

                  <div className="w-px h-6 bg-gray-200 mx-1"></div>

                  <button
                    onClick={resetZoom}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Reset View"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                </div>

                {/* Fullscreen Toggle */}
                <button
                  onClick={handleFullscreen}
                  className="hidden lg:flex p-3 hover:bg-white rounded-xl transition-colors duration-300 group"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                  {isFullscreen ? (
                    <Minimize2 className="w-5 h-5 text-gray-600 group-hover:text-emerald-600" />
                  ) : (
                    <Maximize2 className="w-5 h-5 text-gray-600 group-hover:text-emerald-600" />
                  )}
                </button>

                {/* Close Button */}
                <button
                  onClick={() => {
                    setSelectedImage(null);
                    resetZoom();
                    if (isFullscreen) handleFullscreen();
                  }}
                  className="p-3 hover:bg-white rounded-xl transition-colors duration-300 group"
                  title="Close (ESC)"
                >
                  <X className="w-5 h-5 text-gray-600 group-hover:text-emerald-600" />
                </button>
              </div>
            </div>

            {/* Image Container */}
            <div
              className="flex-1 overflow-hidden relative bg-gray-900"
              onWheel={handleWheel}
            >
              <div
                className="w-full h-full flex items-center justify-center"
                style={{
                  cursor:
                    zoomLevel > 1
                      ? isDragging
                        ? "grabbing"
                        : "grab"
                      : "default",
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <img
                  src={selectedImage.url}
                  alt="Menu"
                  className="max-w-full max-h-full object-contain transition-transform duration-200 select-none"
                  style={{
                    transform: `scale(${zoomLevel}) translate(${position.x}px, ${position.y}px)`,
                    transition: isDragging ? "none" : "transform 0.2s ease",
                  }}
                  draggable="false"
                />
              </div>

              {/* Zoom Instructions */}
              {zoomLevel === 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/75 text-white px-4 py-2 rounded-lg text-sm pointer-events-none backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <span>Scroll to zoom</span>
                    <Move className="w-4 h-4" />
                    <span>Click and drag to pan</span>
                  </div>
                </div>
              )}

              {/* Zoom Indicator */}
              {zoomLevel > 1 && (
                <div className="absolute top-4 right-4 bg-black/75 text-white px-3 py-1.5 rounded-lg text-sm pointer-events-none backdrop-blur-sm">
                  Zoom: {Math.round(zoomLevel * 100)}%
                </div>
              )}
            </div>

            {/* Navigation Dots */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2">
              {menuImages.map((img) => (
                <button
                  key={img.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImage(img);
                    resetZoom();
                  }}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    selectedImage.id === img.id
                      ? "w-6 bg-gradient-to-r from-emerald-400 to-lime-400"
                      : "bg-gray-400 hover:bg-emerald-400"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuGallery;
